import React, { useState, useEffect } from 'react';
import {
  Handshake,
  Building2,
  TrendingUp,
  TrendingDown,
  Users,
  Euro,
  Calendar,
  BarChart3,
  PieChart,
  Plus,
  Filter,
  Search,
  Download,
  Upload,
  Settings,
  AlertCircle,
  CheckCircle,
  Clock,
  Percent,
  Calculator,
  CreditCard,
  Vote,
  FileText,
  Eye,
  Edit,
  Trash2,
  ArrowUpRight,
  ArrowDownRight,
  Target,
  Zap,
  Brain,
  Shield,
  Award,
  Gem
} from 'lucide-react';
import { masterSupabase } from '../lib/supabase-master.js';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../components/notifications/ToastProvider';

const SharedEconomyPage = () => {
  const { user } = useAuth();
  const { showToast } = useToast();
  
  // State Management
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [communities, setCommunities] = useState([]);
  const [selectedCommunity, setSelectedCommunity] = useState('all');
  const [selectedView, setSelectedView] = useState('dashboard');
  const [kpis, setKpis] = useState({
    totalInvestment: 0,
    totalShares: 0,
    monthlyReturn: 0,
    activeInvestments: 0
  });

  // Load Dashboard Data
  useEffect(() => {
    loadDashboardData();
    loadTransactions();
    loadCommunities();
  }, [selectedCommunity]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      
      let query = masterSupabase
        .from('co_ownership_dashboard')
        .select('*');
      
      if (selectedCommunity !== 'all') {
        query = query.eq('community_id', selectedCommunity);
      }
      
      const { data, error } = await query;
      
      if (error) throw error;
      
      setDashboardData(data || []);
      calculateKPIs(data || []);
      
    } catch (error) {
      console.error('Error loading dashboard:', error);
      showToast('Fehler beim Laden der Dashboard-Daten', 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadTransactions = async () => {
    try {
      let query = masterSupabase
        .from('hoa_share_transactions')
        .select(`
          *,
          hoa_units!inner(unit_number, hoa_communities(name)),
          hoa_members!inner(full_name, email)
        `)
        .order('created_at', { ascending: false });
      
      const { data, error } = await query;
      
      if (error) throw error;
      
      setTransactions(data || []);
      
    } catch (error) {
      console.error('Error loading transactions:', error);
    }
  };

  const loadCommunities = async () => {
    try {
      const { data, error } = await masterSupabase
        .from('hoa_communities')
        .select('*')
        .order('name');
      
      if (error) throw error;
      
      setCommunities(data || []);
      
    } catch (error) {
      console.error('Error loading communities:', error);
    }
  };

  const calculateKPIs = (data) => {
    const totalInvestment = data.reduce((sum, item) => sum + (item.share_percentage * 100000), 0);
    const totalShares = data.reduce((sum, item) => sum + item.share_percentage, 0);
    const monthlyReturn = totalInvestment * 0.008; // 0.8% monthly return
    const activeInvestments = data.length;

    setKpis({
      totalInvestment,
      totalShares,
      monthlyReturn,
      activeInvestments
    });
  };

  // KPI Cards Component
  const KPICard = ({ title, value, icon: Icon, trend, color = "blue" }) => (
    <div className="bg-white rounded-lg shadow-sm border p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className={`text-2xl font-bold text-${color}-600`}>{value}</p>
          {trend && (
            <div className={`flex items-center mt-1 text-sm ${trend > 0 ? 'text-green-600' : 'text-red-600'}`}>
              {trend > 0 ? <ArrowUpRight className="w-4 h-4 mr-1" /> : <ArrowDownRight className="w-4 h-4 mr-1" />}
              {Math.abs(trend)}%
            </div>
          )}
        </div>
        <Icon className={`w-8 h-8 text-${color}-600`} />
      </div>
    </div>
  );

  // Investment Opportunity Card
  const InvestmentCard = ({ item }) => (
    <div className="bg-white rounded-lg shadow-sm border p-6 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="font-semibold text-lg">{item.community_name}</h3>
          <p className="text-gray-600">Einheit {item.unit_number}</p>
        </div>
        <div className="text-right">
          <p className="text-2xl font-bold text-green-600">{(item.share_percentage * 100).toFixed(1)}%</p>
          <p className="text-sm text-gray-500">Anteil verfügbar</p>
        </div>
      </div>
      
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div>
          <p className="text-sm text-gray-600">Nutzungstage/Jahr</p>
          <p className="font-semibold">{item.usage_days_per_year}</p>
        </div>
        <div>
          <p className="text-sm text-gray-600">Jahresgebühr</p>
          <p className="font-semibold">{item.annual_usage_fee?.toLocaleString('de-DE')} €</p>
        </div>
      </div>
      
      <div className="flex gap-2">
        <button className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors">
          Investieren
        </button>
        <button className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
          <Eye className="w-4 h-4" />
        </button>
      </div>
    </div>
  );

  // Transaction Row
  const TransactionRow = ({ transaction }) => (
    <tr className="hover:bg-gray-50">
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="flex items-center">
          <div className={`w-2 h-2 rounded-full mr-3 ${
            transaction.transaction_type === 'buy' ? 'bg-green-500' : 
            transaction.transaction_type === 'sell' ? 'bg-red-500' : 'bg-blue-500'
          }`}></div>
          <span className="capitalize">{transaction.transaction_type}</span>
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        {transaction.hoa_units?.hoa_communities?.name} - Einheit {transaction.hoa_units?.unit_number}
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        {(transaction.share_percentage * 100).toFixed(2)}%
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        {transaction.transaction_price?.toLocaleString('de-DE')} €
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <span className={`px-2 py-1 rounded-full text-xs ${
          transaction.transaction_status === 'completed' ? 'bg-green-100 text-green-800' :
          transaction.transaction_status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
          'bg-red-100 text-red-800'
        }`}>
          {transaction.transaction_status}
        </span>
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
        {new Date(transaction.created_at).toLocaleDateString('de-DE')}
      </td>
    </tr>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center">
              <Handshake className="w-8 h-8 mr-3 text-blue-600" />
              Miteigentum
            </h1>
            <p className="text-gray-600 mt-2">
              Intelligente Immobilien-Investments mit geteiltem Eigentum
            </p>
          </div>
          <div className="flex gap-3">
            <button className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center">
              <Plus className="w-4 h-4 mr-2" />
              Neue Investition
            </button>
            <button onClick={() => window.open("/shared-economy/caring", "_blank")} className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-4 py-2 rounded-lg hover:from-purple-700 hover:to-pink-700 transition-colors flex items-center">
              <Zap className="w-4 h-4 mr-2" />
              Sharing is Caring
            </button>
            <button onClick={() => window.open("/shared-economy/landing", "_blank")} className="border border-gray-300 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors flex items-center">
              <Eye className="w-4 h-4 mr-2" />
              Landing Preview
            </button>
            <button className="border border-gray-300 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors">
              <Settings className="w-4 h-4" />
            </button>
          </div>
        </div>
        
        {/* MYNE Homes Killer Badge */}
        <div className="mt-4 inline-flex items-center bg-gradient-to-r from-green-500 to-blue-600 text-white px-4 py-2 rounded-full text-sm font-semibold">
          <Target className="w-4 h-4 mr-2" />
          MYNE HOMES ATTACKE ERFOLGREICH! 🚀
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="mb-6">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            {[
              { id: 'dashboard', label: 'Dashboard', icon: BarChart3 },
              { id: 'investments', label: 'Investitionen', icon: Building2 },
              { id: 'transactions', label: 'Transaktionen', icon: CreditCard },
              { id: 'voting', label: 'Abstimmungen', icon: Vote }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setSelectedView(tab.id)}
                className={`flex items-center py-2 px-1 border-b-2 font-medium text-sm ${
                  selectedView === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <tab.icon className="w-4 h-4 mr-2" />
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Community Filter */}
      <div className="mb-6">
        <select
          value={selectedCommunity}
          onChange={(e) => setSelectedCommunity(e.target.value)}
          className="border border-gray-300 rounded-lg px-3 py-2 bg-white"
        >
          <option value="all">Alle Gemeinschaften</option>
          {communities.map(community => (
            <option key={community.id} value={community.id}>
              {community.name}
            </option>
          ))}
        </select>
      </div>

      {/* Dashboard View */}
      {selectedView === 'dashboard' && (
        <div className="space-y-6">
          {/* KPI Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <KPICard
              title="Gesamtinvestition"
              value={`${kpis.totalInvestment.toLocaleString('de-DE')} €`}
              icon={Euro}
              trend={12.5}
              color="green"
            />
            <KPICard
              title="Aktive Anteile"
              value={`${(kpis.totalShares * 100).toFixed(1)}%`}
              icon={PieChart}
              trend={8.2}
              color="blue"
            />
            <KPICard
              title="Monatliche Rendite"
              value={`${kpis.monthlyReturn.toLocaleString('de-DE')} €`}
              icon={TrendingUp}
              trend={5.7}
              color="purple"
            />
            <KPICard
              title="Investitionen"
              value={kpis.activeInvestments}
              icon={Building2}
              trend={15.3}
              color="orange"
            />
          </div>

          {/* Investment Opportunities */}
          <div>
            <h2 className="text-xl font-semibold mb-4">Verfügbare Investitionen</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {dashboardData.slice(0, 6).map((item, index) => (
                <InvestmentCard key={index} item={item} />
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Transactions View */}
      {selectedView === 'transactions' && (
        <div className="bg-white rounded-lg shadow-sm border">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-semibold">Transaktionshistorie</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Typ
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Immobilie
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Anteil
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Preis
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Datum
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {transactions.map((transaction, index) => (
                  <TransactionRow key={index} transaction={transaction} />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Other views placeholder */}
      {(selectedView === 'investments' || selectedView === 'voting') && (
        <div className="bg-white rounded-lg shadow-sm border p-8 text-center">
          <div className="text-gray-400 mb-4">
            <Brain className="w-16 h-16 mx-auto" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            {selectedView === 'investments' ? 'Investment Portfolio' : 'Abstimmungssystem'}
          </h3>
          <p className="text-gray-600">
            Diese Sektion wird in der nächsten Phase implementiert.
          </p>
        </div>
      )}
    </div>
  );
};

export default SharedEconomyPage;
